import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, CalendarDays, TrendingUp, TrendingDown, Percent, AlertTriangle, ArrowRight, Sparkles, RefreshCw, X, UserPlus, CalendarPlus, CalendarX, CalendarClock, Activity, Crown, BarChart3, Receipt } from "lucide-react";
import { safeFormat } from "@/lib/dateUtils";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, PieChart, Pie, Cell, ReferenceLine } from "recharts";
import { Link } from "react-router-dom";
import { AIContentRenderer } from "@/components/AIContentRenderer";
import ExpandableCard from "@/components/ExpandableCard";
import OnboardingChecklist from "@/components/OnboardingChecklist";
import TeamPerformanceWidget from "@/components/TeamPerformanceWidget";
import { useNavigate } from "react-router-dom";

const EXPENSE_COLORS = { 'Products (Inventory)': '#db2777', Products: '#db2777', Marketing: '#f59e0b', Equipment: '#3b82f6', Utilities: '#10b981', Training: '#8b5cf6', Rent: '#6366f1', Insurance: '#06b6d4', General: '#94a3b8', Other: '#64748b' };
const CHART_COLORS = ['hsl(338,78%,48%)', 'hsl(24,85%,52%)', 'hsl(162,45%,36%)', 'hsl(208,60%,45%)', 'hsl(46,85%,55%)', 'hsl(280,60%,50%)', '#94a3b8'];
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtMonth(ym) { if (!ym || ym.length < 7) return ym; const [y, m] = ym.split('-'); return `${MONTH_NAMES[parseInt(m) - 1]} ${y.slice(2)}`; }
function fmtCurrency(v) { return `£${Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  const hasForecast = payload.some(p => p.dataKey === 'Forecast' && p.value > 0);
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: {typeof p.value === 'number' ? fmtCurrency(p.value) : p.value}</p>
      ))}
      {hasForecast && <p className="text-muted-foreground mt-1 italic">Click the forecast bar for client breakdown</p>}
    </div>
  );
};

// Tooltip for count-based charts (appointments) — no £ sign
const CountTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  const hasForecast = payload.some(p => p.dataKey === 'Forecast' && p.value > 0);
  return (
    <div className="bg-card border border-border rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>{p.name}: {Number(p.value || 0)}</p>
      ))}
      {hasForecast && <p className="text-muted-foreground mt-1 italic">Click the forecast bar for client breakdown</p>}
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [kpis, setKpis] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [rebookNeeded, setRebookNeeded] = useState([]);
  const [charts, setCharts] = useState({});
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityFilter, setActivityFilter] = useState('All');
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [apptMode, setApptMode] = useState('month');
  const [tenantSettings, setTenantSettings] = useState({});
  const [showForecast, setShowForecast] = useState(false);
  const [forecastBreakdown, setForecastBreakdown] = useState(null);
  const [forecastBreakdownLoading, setForecastBreakdownLoading] = useState(false);
  const [excludeComp, setExcludeComp] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings?lite=true`).then(r => setTenantSettings(r.data || {})).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const fp = selectedMonths.length > 0 ? `&filter_months=${selectedMonths.join(',')}` : '';
      const cp = excludeComp ? `&exclude_comp=true` : '';
      const [kpiRes, upRes, rbRes, chRes, raRes] = await Promise.all([
        axios.get(`${API}/dashboard/kpis?days=30${cp}`),
        axios.get(`${API}/dashboard/upcoming`),
        axios.get(`${API}/dashboard/rebook-needed`),
        axios.get(`${API}/dashboard/charts?months=12${fp}${cp}`),
        axios.get(`${API}/dashboard/recent-activity?limit=20`)
      ]);
      setKpis(kpiRes.data);
      setUpcoming(upRes.data.appointments || []);
      setRebookNeeded(rbRes.data.clients || []);
      setCharts(chRes.data);
      setRecentActivity(raRes.data.activity || []);
      if (chRes.data.available_months) setAvailableMonths(chRes.data.available_months);
    } catch (e) {  }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, [selectedMonths, excludeComp]);

  const generateSummary = async () => {
    setLoadingSummary(true);
    try { const res = await axios.get(`${API}/ai/daily-summary`); setAiSummary(res.data.summary); } catch (e) {  }
    setLoadingSummary(false);
  };

  const toggleMonth = (m) => setSelectedMonths(p => p.includes(m) ? p.filter(x => x !== m) : [...p, m]);

  const statusColors = { Confirmed: '#22c55e', Completed: '#3b82f6', Cancelled: '#ef4444', Pending: '#eab308', 'No-show': '#6b7280' };
  const monthlySales = (charts.monthly_sales || []).map(d => ({ ...d, name: fmtMonth(d.month), 'Completed': Math.round(d.completed_sales || 0), 'Upcoming': Math.round(d.upcoming_sales || 0), 'Forecast': Math.round(d.forecast_sales || 0) }));
  const monthlyAppts = (charts.monthly_sales || []).map(d => ({ ...d, name: fmtMonth(d.month), 'Completed': d.completed_count || 0, 'Upcoming': d.upcoming_count || 0, 'Forecast': d.forecast_count || 0 }));
  const dailyAppts = (charts.daily_data || []).map(d => ({ ...d, name: d.day?.slice(8) || '', 'Completed': d.completed || 0, 'Upcoming': d.upcoming || 0 }));

  const openForecastBreakdown = async (monthKey, unit) => {
    if (!monthKey) return;
    setForecastBreakdownLoading(true);
    setForecastBreakdown({ month: monthKey, unit, total: 0, count: 0, clients: [] });
    try {
      const res = await axios.get(`${API}/dashboard/forecast-breakdown`, { params: { month: monthKey } });
      setForecastBreakdown({ unit, ...res.data });
    } catch (e) {
      toast.error('Failed to load forecast breakdown');
    }
    setForecastBreakdownLoading(false);
  };

  // Expenses by month
  const expCats = charts.expense_categories || [];
  const monthlyExp = (charts.monthly_expenses || []).map(d => ({ ...d, name: fmtMonth(d.month) }));

  // Monthly profit
  const monthlyProfit = (charts.monthly_profit || []).map(d => ({ ...d, name: fmtMonth(d.month) }));

  // Service donut
  const serviceData = (charts.service_breakdown || []).map((d, i) => ({ ...d, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  const serviceTotal = serviceData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="space-y-5 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Dashboard</h1>
          <p className="text-sm text-muted-foreground">Welcome back! Here's what's happening.</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} className="rounded-xl"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>

      {/* Onboarding checklist — auto-hides when complete or dismissed */}
      <OnboardingChecklist />

      {/* Team / single-operator performance widget */}
      <TeamPerformanceWidget currencySymbol={tenantSettings.currency_symbol || '£'} />

      {/* KPI Cards */}
      <TooltipProvider delayDuration={200}>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">

        {/* Active Clients — clickable → /admin/clients */}
        <Link to="/admin/clients" data-testid="kpi-active-clients">
          <Card className="p-3 rounded-2xl border shadow-sm card-hover cursor-pointer hover:border-primary/40 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] text-muted-foreground font-medium">Active Clients</span>
              <Users size={14} className="text-primary" />
            </div>
            <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{kpis.active_clients || 0}</p>
          </Card>
        </Link>

        {/* New Clients — hover shows names */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="p-3 rounded-2xl border shadow-sm card-hover cursor-default" data-testid="kpi-new-clients">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-muted-foreground font-medium">New Clients</span>
                <UserPlus size={14} className="text-green-500" />
              </div>
              <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{kpis.new_clients_month || 0}</p>
            </Card>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[200px] p-0 overflow-hidden rounded-xl shadow-xl border">
            <div className="bg-card px-3 py-2 border-b">
              <p className="text-xs font-semibold">New this month</p>
            </div>
            {(kpis.new_clients_list || []).length > 0 ? (
              <div className="px-3 py-2 space-y-1 max-h-48 overflow-y-auto">
                {(kpis.new_clients_list || []).map((name, i) => (
                  <p key={`nc-${name}-${i}`} className="text-xs text-muted-foreground">• {name}</p>
                ))}
              </div>
            ) : (
              <p className="px-3 py-2 text-xs text-muted-foreground">No new clients yet this month</p>
            )}
          </TooltipContent>
        </Tooltip>

        {/* Today's Bookings — hover shows time + service */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className="p-3 rounded-2xl border shadow-sm card-hover cursor-default" data-testid="kpi-todays-bookings">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-muted-foreground font-medium">Today's Bookings</span>
                <CalendarDays size={14} className="text-blue-500" />
              </div>
              <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{kpis.todays_bookings || 0}</p>
            </Card>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[240px] p-0 overflow-hidden rounded-xl shadow-xl border">
            <div className="bg-card px-3 py-2 border-b">
              <p className="text-xs font-semibold">Today's schedule</p>
            </div>
            {(kpis.todays_bookings_list || []).length > 0 ? (
              <div className="px-3 py-2 space-y-2 max-h-56 overflow-y-auto">
                {(kpis.todays_bookings_list || []).map((appt, i) => (
                  <div key={`tb-${appt.time}-${appt.client_name}`} className="flex items-start gap-2">
                    <span className="text-[10px] font-mono font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5 shrink-0">{appt.time}</span>
                    <div className="min-w-0">
                      <p className="text-xs font-medium leading-tight">{appt.client_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{appt.services}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-3 py-2 text-xs text-muted-foreground">No bookings today</p>
            )}
          </TooltipContent>
        </Tooltip>

        {/* Total Sales — this month with % change vs last month */}
        <Card className="p-3 rounded-2xl border shadow-sm card-hover" data-testid="kpi-total-sales">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground font-medium">This Month Sales</span>
            <TrendingUp size={14} className="text-green-500" />
          </div>
          <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>
            {fmtCurrency(kpis.this_month_sales ?? kpis.total_sales)}
          </p>
          {kpis.month_pct_change !== undefined && kpis.last_month_sales > 0 && (
            <div className={`flex items-center gap-0.5 mt-1 text-[10px] font-medium ${kpis.month_pct_change >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {kpis.month_pct_change >= 0
                ? <TrendingUp size={10} />
                : <TrendingDown size={10} />}
              {kpis.month_pct_change >= 0 ? '+' : ''}{kpis.month_pct_change}% vs last month
            </div>
          )}
          {kpis.month_pct_change !== undefined && kpis.last_month_sales === 0 && kpis.this_month_sales > 0 && (
            <p className="text-[10px] text-green-600 mt-1 font-medium">↑ First sales this month!</p>
          )}
          <p className="text-[9px] text-muted-foreground mt-0.5">Incl. upcoming confirmed</p>
        </Card>

        {/* This Week vs Last Week Sales (Mon-Sun) */}
        <Card className="p-3 rounded-2xl border shadow-sm card-hover" data-testid="kpi-week-sales">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground font-medium">This Week Sales</span>
            <BarChart3 size={14} className="text-indigo-500" />
          </div>
          <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }} data-testid="kpi-week-sales-value">
            {fmtCurrency(kpis.this_week_sales)}
          </p>
          {kpis.week_pct_change !== undefined && kpis.last_week_sales > 0 && (
            <div className={`flex items-center gap-0.5 mt-1 text-[10px] font-medium ${kpis.week_pct_change >= 0 ? 'text-green-600' : 'text-red-500'}`} data-testid="kpi-week-sales-change">
              {kpis.week_pct_change >= 0
                ? <TrendingUp size={10} />
                : <TrendingDown size={10} />}
              {kpis.week_pct_change >= 0 ? '+' : ''}{kpis.week_pct_change}% vs last week
            </div>
          )}
          {kpis.week_pct_change !== undefined && kpis.last_week_sales === 0 && kpis.this_week_sales > 0 && (
            <p className="text-[10px] text-green-600 mt-1 font-medium">↑ First sales this week!</p>
          )}
          <p className="text-[9px] text-muted-foreground mt-0.5">Mon–Sun · was {fmtCurrency(kpis.last_week_sales)}</p>
        </Card>

        {/* Retention — plain card */}
        <Card className="p-3 rounded-2xl border shadow-sm card-hover" data-testid="kpi-retention">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground font-medium">Retention</span>
            <Percent size={14} className="text-orange-500" />
          </div>
          <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{kpis.retention_pct || 0}%</p>
        </Card>

        {/* This Month Expenses — % change vs last month (up is bad, down is good) */}
        <Card className="p-3 rounded-2xl border shadow-sm card-hover" data-testid="kpi-expenses">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-muted-foreground font-medium">This Month Expenses</span>
            <Receipt size={14} className="text-rose-500" />
          </div>
          <p className="text-xl font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }} data-testid="kpi-expenses-value">
            {fmtCurrency(kpis.this_month_expenses)}
          </p>
          {kpis.expenses_pct_change !== undefined && kpis.last_month_expenses > 0 && (
            <div className={`flex items-center gap-0.5 mt-1 text-[10px] font-medium ${kpis.expenses_pct_change > 0 ? 'text-red-500' : 'text-green-600'}`} data-testid="kpi-expenses-change">
              {kpis.expenses_pct_change > 0
                ? <TrendingUp size={10} />
                : <TrendingDown size={10} />}
              {kpis.expenses_pct_change >= 0 ? '+' : ''}{kpis.expenses_pct_change}% vs last month
            </div>
          )}
          {kpis.expenses_pct_change !== undefined && kpis.last_month_expenses === 0 && kpis.this_month_expenses > 0 && (
            <p className="text-[10px] text-red-500 mt-1 font-medium">↑ First expenses this month</p>
          )}
        </Card>

        {/* Low Stock — hover shows items + qty */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Card className={`p-3 rounded-2xl border shadow-sm card-hover cursor-default ${kpis.low_stock_alerts > 0 ? 'border-red-200 bg-red-50/30' : ''}`} data-testid="kpi-low-stock">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-muted-foreground font-medium">Low Stock</span>
                <AlertTriangle size={14} className={kpis.low_stock_alerts > 0 ? 'text-red-500' : 'text-green-500'} />
              </div>
              <p className={`text-xl font-semibold tabular-nums ${kpis.low_stock_alerts > 0 ? 'text-red-600' : ''}`} style={{ fontFamily: 'Playfair Display' }}>{kpis.low_stock_alerts || 0}</p>
            </Card>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[220px] p-0 overflow-hidden rounded-xl shadow-xl border">
            <div className="bg-card px-3 py-2 border-b">
              <p className="text-xs font-semibold text-red-600">Low stock items</p>
            </div>
            {(kpis.low_stock_items || []).length > 0 ? (
              <div className="px-3 py-2 space-y-1.5 max-h-48 overflow-y-auto">
                {(kpis.low_stock_items || []).map((item, i) => (
                  <div key={`ls-${item.name}`} className="flex items-center justify-between gap-3">
                    <p className="text-xs text-muted-foreground truncate">{item.name}</p>
                    <span className={`text-[10px] font-semibold shrink-0 px-1.5 py-0.5 rounded ${item.uses_on_hand <= 3 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                      {item.uses_on_hand} uses
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="px-3 py-2 text-xs text-muted-foreground">All stock levels OK</p>
            )}
          </TooltipContent>
        </Tooltip>

      </div>
      </TooltipProvider>

      {/* Month Filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-muted-foreground">Filter:</span>
          {selectedMonths.length > 0 && <button onClick={() => setSelectedMonths([])} className="text-[10px] px-2 py-1 rounded-full bg-muted hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"><X size={10} /> Clear</button>}
          {availableMonths.slice(-12).map(m => (
            <button key={m} onClick={() => toggleMonth(m)} className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${selectedMonths.includes(m) ? 'bg-primary text-white' : 'bg-muted/60 text-muted-foreground hover:bg-primary/10'}`}>{fmtMonth(m)}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-muted-foreground font-medium">Hide Complimentary</span>
          <Switch checked={excludeComp} onCheckedChange={setExcludeComp} data-testid="exclude-comp-toggle" />
        </div>
      </div>
      {excludeComp && (
        <p className="text-[10px] text-muted-foreground -mt-3" data-testid="exclude-comp-explainer">
          Complimentary (comp'd) appointments are excluded from the revenue and appointment totals below.
        </p>
      )}

      {/* Row 1: Monthly Sales (stacked) + AI Summary */}
      <div className="grid lg:grid-cols-3 gap-5">
        <ExpandableCard title="Monthly Sales" className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4 pr-8">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Monthly Sales</h3>
            <div className="flex items-center gap-2 mr-2">
              <span className="text-[10px] text-muted-foreground font-medium">Forecast</span>
              <Switch checked={showForecast} onCheckedChange={setShowForecast} data-testid="forecast-toggle" />
            </div>
          </div>
          {showForecast && (
            <p className="text-[10px] text-muted-foreground -mt-2 mb-3" data-testid="forecast-explainer">
              Hatched bars: projected revenue if every Active/VIP client rebooks on their own ~4-week cycle, excluding months/clients already covered by real bookings.
            </p>
          )}
          {monthlySales.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={monthlySales} barCategoryGap="20%">
                <defs>
                  <pattern id="forecastHatch" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                    <rect width="6" height="6" fill="#ede9fe" />
                    <line x1="0" y1="0" x2="0" y2="6" stroke="#a78bfa" strokeWidth="2" />
                  </pattern>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(330,28%,90%)" vertical={false} />
                <XAxis dataKey="name" fontSize={10} tick={{ fill: '#6b7280' }} />
                <YAxis fontSize={10} tick={{ fill: '#6b7280' }} tickFormatter={v => `£${v}`} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                <Bar dataKey="Completed" stackId="sales" fill="#db2777" radius={[0,0,0,0]} />
                <Bar dataKey="Upcoming" stackId="sales" fill="#f9a8d4" radius={showForecast ? [0,0,0,0] : [4,4,0,0]} />
                {showForecast && (
                  <Bar dataKey="Forecast" stackId="sales" fill="url(#forecastHatch)" stroke="#a78bfa" strokeWidth={1} radius={[4,4,0,0]} cursor="pointer" onClick={(data) => openForecastBreakdown(data.month, 'currency')} data-testid="forecast-bar-sales" />
                )}
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[240px] flex items-center justify-center text-muted-foreground text-sm">No data</div>}
        </ExpandableCard>
        <Card className="p-5 rounded-2xl border shadow-sm overflow-hidden">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-5 h-5 rounded-lg object-cover" />
              <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>
                Beyond <span style={{ color: '#2DD4BF' }}>AI</span>
              </h3>
            </div>
            <span className="text-[9px] font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: 'linear-gradient(135deg,#1F6FE5 0%,#2DD4BF 100%)' }}>Daily briefing</span>
          </div>
          {aiSummary ? (
            <div className="max-h-[240px] overflow-y-auto pr-1 fade-in"><AIContentRenderer content={aiSummary} compact={true} /></div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Beyond AI · a calm, clear read of today's operations.</p>
              <Button onClick={generateSummary} disabled={loadingSummary} size="sm" className="rounded-xl w-full text-white border-0" style={{ background: loadingSummary ? undefined : 'linear-gradient(135deg,#1F6FE5 0%,#2DD4BF 100%)' }}>{loadingSummary ? 'Beyond AI is analysing…' : 'Generate with Beyond AI'}</Button>
            </div>
          )}
        </Card>
      </div>

      {/* Row 2: Appointments (stacked) + Sales by Service */}
      <div className="grid lg:grid-cols-2 gap-5">
        <ExpandableCard title="Appointments">
          <div className="flex items-center justify-between mb-4 pr-8">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Appointments</h3>
            <div className="flex items-center gap-3 mr-2">
              {apptMode === 'month' && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground font-medium">Forecast</span>
                  <Switch checked={showForecast} onCheckedChange={setShowForecast} data-testid="forecast-toggle-appts" />
                </div>
              )}
              <Tabs value={apptMode} onValueChange={setApptMode}>
                <TabsList className="h-7 rounded-lg"><TabsTrigger value="month" className="text-[10px] h-5 rounded px-2">Month</TabsTrigger><TabsTrigger value="day" className="text-[10px] h-5 rounded px-2">Day</TabsTrigger></TabsList>
              </Tabs>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={apptMode === 'month' ? monthlyAppts : dailyAppts} barCategoryGap="15%">
              <defs>
                <pattern id="forecastHatchAppts" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                  <rect width="6" height="6" fill="#ede9fe" />
                  <line x1="0" y1="0" x2="0" y2="6" stroke="#a78bfa" strokeWidth="2" />
                </pattern>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(330,28%,90%)" vertical={false} />
              <XAxis dataKey="name" fontSize={10} tick={{ fill: '#6b7280' }} />
              <YAxis fontSize={10} tick={{ fill: '#6b7280' }} allowDecimals={false} tickFormatter={v => Math.round(v)} />
              <RechartsTooltip content={<CountTooltip />} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
              <Bar dataKey="Completed" stackId="appts" fill="#3b82f6" radius={[0,0,0,0]} />
              <Bar dataKey="Upcoming" stackId="appts" fill="#93c5fd" radius={(showForecast && apptMode === 'month') ? [0,0,0,0] : [4,4,0,0]} />
              {showForecast && apptMode === 'month' && (
                <Bar dataKey="Forecast" stackId="appts" fill="url(#forecastHatchAppts)" stroke="#a78bfa" strokeWidth={1} radius={[4,4,0,0]} cursor="pointer" onClick={(data) => openForecastBreakdown(data.month, 'count')} data-testid="forecast-bar-appts" />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ExpandableCard>
        <ExpandableCard title="Sales by Service">
          <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Sales by Service</h3>
          {serviceData.length > 0 ? (
            <div className="flex items-center gap-4">
              <ResponsiveContainer width="45%" height={200}>
                <PieChart><Pie data={serviceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>{serviceData.map((d) => <Cell key={d.name} fill={d.fill} />)}</Pie><RechartsTooltip formatter={v => fmtCurrency(v)} /></PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-1">
                {serviceData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5 min-w-0"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.fill }} /><span className="truncate">{d.name}</span></div>
                    <div className="flex items-center gap-2 shrink-0 tabular-nums"><span className="text-muted-foreground">{serviceTotal > 0 ? Math.round(d.value / serviceTotal * 100) : 0}%</span><span className="font-medium">{fmtCurrency(d.value)}</span></div>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data</div>}
        </ExpandableCard>
      </div>

      {/* Row 3: Monthly Expenses + Monthly Profit */}
      <div className="grid lg:grid-cols-2 gap-5">
        <ExpandableCard title="Monthly Expenses">
          <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Monthly Expenses</h3>
          {monthlyExp.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyExp} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(330,28%,90%)" vertical={false} />
                <XAxis dataKey="name" fontSize={10} tick={{ fill: '#6b7280' }} />
                <YAxis fontSize={10} tick={{ fill: '#6b7280' }} tickFormatter={v => `£${v}`} />
                <RechartsTooltip content={<ChartTooltip />} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                {expCats.map((cat, i) => (
                  <Bar key={cat} dataKey={cat} stackId="exp" fill={EXPENSE_COLORS[cat] || CHART_COLORS[i % CHART_COLORS.length]} radius={i === expCats.length - 1 ? [4,4,0,0] : [0,0,0,0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No expense data</div>}
        </ExpandableCard>
        <ExpandableCard title="Monthly Profit">
          <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>Monthly Profit</h3>
          {monthlyProfit.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyProfit}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(330,28%,90%)" vertical={false} />
                <XAxis dataKey="name" fontSize={10} tick={{ fill: '#6b7280' }} />
                <YAxis fontSize={10} tick={{ fill: '#6b7280' }} tickFormatter={v => `£${v}`} />
                <RechartsTooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.[0]) return null;
                  const d = payload[0].payload;
                  return (<div className="bg-card border rounded-xl p-3 shadow-lg text-xs"><p className="font-semibold mb-1">{label}</p><p>Revenue: {fmtCurrency(d.revenue)}</p><p>Expenses: {fmtCurrency(d.expenses)}</p><p className={`font-semibold ${d.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>Profit: {fmtCurrency(d.profit)}</p></div>);
                }} />
                <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                <Bar dataKey="profit" radius={[4,4,0,0]}>
                  {monthlyProfit.map((d) => <Cell key={d.name} fill={d.profit >= 0 ? '#22c55e' : '#ef4444'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-[200px] flex items-center justify-center text-muted-foreground text-sm">No data</div>}
        </ExpandableCard>
      </div>

      {/* Booking Status */}
      {(charts.booking_status || []).length > 0 && (
        <Card className="p-4 rounded-2xl border shadow-sm">
          <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Playfair Display' }}>Booking Status</h3>
          <div className="flex items-center gap-6 flex-wrap">
            {(charts.booking_status || []).map((s) => (
              <div key={s.name} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ background: statusColors[s.name] || '#94a3b8' }} />
                <span className="text-xs font-medium">{s.name}</span>
                <span className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{s.value}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Booking Activity */}
      <Card className="p-5 rounded-2xl border shadow-sm" data-testid="recent-activity-card">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-primary" />
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Recent Booking Activity</h3>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {['All', 'Booked', 'Changed', 'Cancelled'].map(f => (
              <button
                key={f}
                onClick={() => setActivityFilter(f)}
                data-testid={`activity-filter-${f.toLowerCase()}`}
                className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${activityFilter === f ? 'bg-primary text-white' : 'bg-muted/60 text-muted-foreground hover:bg-primary/10'}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        {(() => {
          const filtered = activityFilter === 'All' ? recentActivity : recentActivity.filter(a => a.action === activityFilter);
          if (filtered.length === 0) {
            return <p className="text-sm text-muted-foreground py-4">No recent activity.</p>;
          }
          const actionMeta = {
            Booked: { icon: CalendarPlus, cls: 'bg-green-100 text-green-700 border-green-200' },
            Changed: { icon: CalendarClock, cls: 'bg-amber-100 text-amber-700 border-amber-200' },
            Cancelled: { icon: CalendarX, cls: 'bg-red-100 text-red-700 border-red-200' }
          };
          return (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-xs min-w-[640px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b border-border/60">
                    <th className="text-left font-medium px-2 py-2">Action</th>
                    <th className="text-left font-medium px-2 py-2">Client</th>
                    <th className="text-left font-medium px-2 py-2">Service</th>
                    <th className="text-left font-medium px-2 py-2">Appointment</th>
                    <th className="text-left font-medium px-2 py-2">Booked</th>
                    <th className="text-right font-medium px-2 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const meta = actionMeta[a.action] || actionMeta.Booked;
                    const Icon = meta.icon;
                    return (
                      <tr
                        key={`${a.id}-${a.action_at}`}
                        data-testid={`activity-row-${a.id}`}
                        onClick={() => navigate(`/admin/appointments?focus=${a.id}`)}
                        className="border-b border-border/40 last:border-0 hover:bg-muted/40 transition-colors cursor-pointer group"
                        title="Open appointment"
                      >
                        <td className="px-2 py-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${meta.cls}`}>
                            <Icon size={10} /> {a.action}
                          </span>
                        </td>
                        <td className="px-2 py-2 font-medium truncate max-w-[140px]">
                          <div className="flex items-center gap-1.5">
                            {a.client_id ? (
                              <Link
                                to={`/admin/clients/${a.client_id}`}
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`activity-client-link-${a.id}`}
                                className="hover:text-primary hover:underline underline-offset-2 truncate"
                              >
                                {a.client_name}
                              </Link>
                            ) : (
                              <span className="truncate">{a.client_name}</span>
                            )}
                            {a.is_vip_reward && (
                              <span
                                title={`VIP reward redeemed: ${a.voucher_code_applied} (-£${a.voucher_amount_applied?.toFixed?.(2) ?? a.voucher_amount_applied})`}
                                data-testid={`activity-vip-reward-${a.id}`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-amber-100 text-amber-800 border border-amber-300 shrink-0"
                              >
                                <Crown size={9} /> VIP
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-muted-foreground truncate max-w-[180px]">{a.services || '—'}</td>
                        <td className="px-2 py-2 whitespace-nowrap">{safeFormat(a.appointment_datetime, 'EEE d MMM, HH:mm')}</td>
                        <td className="px-2 py-2 text-muted-foreground whitespace-nowrap">{safeFormat(a.action_at, 'd MMM, HH:mm')}</td>
                        <td className="px-2 py-2 text-right tabular-nums font-medium">{fmtCurrency(a.total_price)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </Card>

      {/* Operational Panels */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5 rounded-2xl border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Upcoming Appointments</h3>
            <Link to="/admin/appointments"><Button variant="ghost" size="sm" className="text-xs">View all <ArrowRight size={12} className="ml-1" /></Button></Link>
          </div>
          {upcoming.length > 0 ? (
            <div className="space-y-2">{upcoming.slice(0, 8).map(a => (
              <div key={a.id} className="flex items-center justify-between p-2 rounded-xl hover:bg-muted/30 transition-colors text-xs">
                <div className="min-w-0"><p className="font-medium">{a.client_name}</p><p className="text-muted-foreground">{(a.services||[]).map(s=>s.name).join(', ')}</p></div>
                <div className="text-right shrink-0"><p className="font-medium">{safeFormat(a.appointment_datetime, 'EEE d MMM')}</p><p className="text-muted-foreground">{safeFormat(a.appointment_datetime, 'HH:mm')}</p></div>
              </div>
            ))}</div>
          ) : <p className="text-sm text-muted-foreground py-4">No upcoming appointments.</p>}
        </Card>
        <Card className="p-5 rounded-2xl border shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>Clients Needing Rebook</h3>
            <Link to="/admin/clients"><Button variant="ghost" size="sm" className="text-xs">View all <ArrowRight size={12} className="ml-1" /></Button></Link>
          </div>
          {rebookNeeded.length > 0 ? (
            <div className="space-y-2">{rebookNeeded.slice(0, 8).map(c => (
              <Link key={c.id} to={`/admin/clients/${c.id}`} className="flex items-center justify-between p-2 rounded-xl hover:bg-muted/30 transition-colors text-xs">
                <div><p className="font-medium">{c.name}</p><p className="text-muted-foreground">{c.email}</p></div>
                <Badge variant="outline" className="text-[10px] shrink-0">{c.days_since}d ago</Badge>
              </Link>
            ))}</div>
          ) : <p className="text-sm text-muted-foreground py-4">All clients up to date!</p>}
        </Card>
      </div>

      <Dialog open={!!forecastBreakdown} onOpenChange={(o) => { if (!o) setForecastBreakdown(null); }}>
        <DialogContent className="rounded-2xl max-w-md max-h-[80vh] overflow-y-auto" data-testid="forecast-breakdown-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>
              Forecast breakdown — {forecastBreakdown && fmtMonth(forecastBreakdown.month)}
            </DialogTitle>
          </DialogHeader>
          {forecastBreakdownLoading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-fuchsia-50 border border-fuchsia-200 rounded-xl">
                <span className="text-xs text-muted-foreground">Total projected</span>
                <span className="font-semibold text-fuchsia-700" data-testid="forecast-breakdown-total">
                  {forecastBreakdown?.unit === 'count'
                    ? `${forecastBreakdown?.count || 0} appointment${forecastBreakdown?.count === 1 ? '' : 's'}`
                    : fmtCurrency(forecastBreakdown?.total || 0)}
                </span>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {(forecastBreakdown?.clients || []).map(c => (
                  <div key={c.client_id} className="flex items-center justify-between py-2 text-sm" data-testid={`forecast-breakdown-client-${c.client_id}`}>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{c.client_name}</p>
                      <p className="text-[10px] text-muted-foreground">Projected {safeFormat(c.projected_date, 'd MMM')}</p>
                    </div>
                    <span className="tabular-nums text-fuchsia-700 font-medium shrink-0 ml-3">£{(c.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
                {(forecastBreakdown?.clients || []).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-6">No clients projected for this month.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
